/** @format */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Divider,
  Stack,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ModeButtons from '../../../pages/Home/components/ModeButtons';
import WorkroomStatusPanel from '../components/WorkroomStatusPanel';
import WorkroomCarousel, {
  WORKROOM_CAROUSEL_ACCENT,
} from '../components/WorkroomCarousel';
import { mobileNavMediaQuery } from '../../../layout/layoutMetrics';
import ActivationForm from '../components/Workrooms/Activations/ActivationForm/ActivationForm';
import ActivationList from '../components/Workrooms/Activations/ActivationList/ActivationList';
import {
  getProjectWorkRoomByProjectId,
  viewProjectWorkRoomImage,
  createActivation,
  updateActivation,
  getActivations,
} from '../../../core/services/projectWorkRoom.service';
import ViewActivation from '../components/Workrooms/Activations/ViewActivation/ViewActivation';
import {
  WORKROOM_STEP,
  WORKROOM_UPDATED_EVENT,
  dispatchEditWorkroom,
  dispatchCreateWorkroom,
} from '../../../layout/workroomEditEvents';
import { useAuth } from '../../../app/context/AuthContext';
import { MODULE_LIST } from '../../../constants/commons';
import { isVectorAssetFileName } from '../../../core/services/fileUpload.service';

const IMAGE_SLOTS = 4;

function unwrapWorkroom(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload.find(Boolean) ?? null;
  if (payload?.data && typeof payload.data === 'object') return payload.data;
  return payload;
}

function getImageNames(workroom) {
  const mediaFiles = workroom?.mediaFiles ?? workroom?.MediaFiles ?? {};
  const images = mediaFiles?.images ?? mediaFiles?.Images ?? [];
  return (Array.isArray(images) ? images : []).map(String).filter(Boolean);
}

function ConceptImageGrid({ projectId, imageNames, onAddRef }) {
  const [imageUrls, setImageUrls] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const { userPermissions } = useAuth();
  const workroomModule = Object.hasOwn(userPermissions, MODULE_LIST.WORKROOM)
    ? userPermissions[MODULE_LIST.WORKROOM]
    : false;
  const canEditWorkroom = workroomModule.edit;

  useEffect(() => {
    if (!projectId || !imageNames.length) {
      setImageUrls([]);
      setImagesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];

    const loadImages = async () => {
      setImagesLoading(true);
      try {
        const urls = await Promise.all(
          imageNames.map((name) =>
            isVectorAssetFileName(name)
              ? Promise.resolve(null)
              : viewProjectWorkRoomImage(projectId, name),
          )
        );

        if (cancelled) {
          urls.forEach((url) => {
            if (url) URL.revokeObjectURL(url);
          });
          return;
        }

        objectUrls.push(...urls.filter(Boolean));
        setImageUrls(urls);
      } finally {
        if (!cancelled) setImagesLoading(false);
      }
    };

    loadImages();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [projectId, imageNames]);

  const slots = useMemo(() => {
    const filled = imageUrls.slice(0, IMAGE_SLOTS).map((url, index) => ({
      id: imageNames[index] ?? `image-${index}`,
      url,
      isVector: isVectorAssetFileName(imageNames[index]),
    }));

    while (filled.length < IMAGE_SLOTS) {
      filled.push({ id: `placeholder-${filled.length}`, url: null, isVector: false });
    }

    return filled.slice(0, IMAGE_SLOTS);
  }, [imageNames, imageUrls]);

  const carouselImages = useMemo(
    () =>
      imageUrls
        .map((url, index) => ({
          id: imageNames[index] ?? `image-${index}`,
          url,
        }))
        .filter((image) => Boolean(image.url)),
    [imageNames, imageUrls]
  );

  const openCarouselAt = (slotId) => {
    const index = carouselImages.findIndex((image) => image.id === slotId);
    if (index < 0) return;
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const rows = [slots.slice(0, 2), slots.slice(2, 4)];
  const pendingSlots = imagesLoading
    ? imageNames.slice(0, IMAGE_SLOTS).length
    : 0;

  return (
    <Box>
      {rows.map((row, rowIndex) => (
        <Stack
          key={`row-${rowIndex}`}
          direction="row"
          spacing={1}
          sx={{ mb: rowIndex === 0 ? 1 : 0 }}
        >
          {row.map((slot, slotIndex) => {
            const absoluteIndex = rowIndex * 2 + slotIndex;
            const isFetching = imagesLoading && absoluteIndex < pendingSlots;
            const canOpen = Boolean(slot.url) && !imagesLoading;
            return (
              <Box
                key={slot.id}
                title={slot.isVector ? slot.id : undefined}
                role={canOpen ? 'button' : undefined}
                tabIndex={canOpen ? 0 : undefined}
                aria-label={canOpen ? `View ${slot.id}` : undefined}
                onClick={() => canOpen && openCarouselAt(slot.id)}
                onKeyDown={(event) => {
                  if (!canOpen) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openCarouselAt(slot.id);
                  }
                }}
                sx={{
                  width: { xs: 72, sm: 85 },
                  height: { xs: 72, sm: 85 },
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.1)',
                  overflow: 'hidden',
                  bgcolor: slot.url
                    ? 'transparent'
                    : slot.isVector
                      ? 'rgba(168, 85, 247, 0.12)'
                      : 'rgba(255,255,255,0.06)',
                  backgroundImage: slot.url ? `url(${slot.url})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: canOpen ? 'pointer' : 'default',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': canOpen
                    ? {
                        transform: 'scale(1.03)',
                        boxShadow: `0 0 0 1px ${WORKROOM_CAROUSEL_ACCENT}88`,
                      }
                    : undefined,
                  '&:focus-visible': canOpen
                    ? {
                        outline: `2px solid ${WORKROOM_CAROUSEL_ACCENT}`,
                        outlineOffset: 2,
                      }
                    : undefined,
                }}
              >
                {isFetching ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: 'rgba(255,255,255,0.45)' }}
                  />
                ) : slot.isVector ? (
                  <InsertDriveFileOutlinedIcon
                    titleAccess={slot.id}
                    sx={{ color: '#A855F7', fontSize: 28 }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Stack>
      ))}
      <Typography
        onClick={() => onAddRef?.()}
        sx={{
          fontSize: 12,
          opacity: 0.4,
          mt: 1,
          textAlign: { xs: 'left', sm: 'right' },
          cursor: onAddRef ? 'pointer' : 'default',
          '&:hover': onAddRef ? { opacity: 0.7 } : undefined,
          display: canEditWorkroom ? 'block' : 'none',
        }}
      >
        + add ref
      </Typography>

      <WorkroomCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />
    </Box>
  );
}

function buildActivationFormData(form) {
  // 1. Normalize variants: rename variantsTitle -> variantTitle, etc.
  const variants = (form.variants || []).map((v) => ({
    variantsTitle: v.variantsTitle ?? v.variantTitle ?? '',
    variantsDescription: v.variantsDescription ?? v.variantDescription ?? '',
    variantsPoints: v.variantsPoints ?? v.variantsPoints ?? [],
    hookName: v.hookName ?? '',
    ...(v.variantsId ? { variantsId: v.variantsId } : {}),
  }));

  // 3. Assemble FormData
  const formData = new FormData();
  formData.append('activationName', form.name);
  formData.append('projectId', form.projectId);
  formData.append('description', form.description);
  // formData.append('height');
  // formData.append('width');
  // formData.append('sequence');
  formData.append(
    'activationPoints',
    form.activationPoints.map((item) => `${item}`).join(',')
  );
  formData.append('variants', JSON.stringify(variants));

  // Files: append actual File/Blob objects individually (not JSON.stringify-able)
  (form.files || []).forEach((file) => {
    if (file instanceof File || file instanceof Blob) {
      formData.append('files', file);
    }
  });

  return formData;
}

export default function ProjectViewPage() {
  const { projectId } = useParams();
  const [workroom, setWorkroom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activations, setActivations] = useState({
    activeActivation: null,
    modal: false, // for edit form
    viewMode: false, // for view mode
    list: [], // all activations
    defaultStep: null, // to jump to a specific step while updating.
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Only tighten left gutter when Key Dates would wrap to two lines.
  const [expandTimelineLayout, setExpandTimelineLayout] = useState(false);
  const handleTimelineExpandedLayout = useCallback((needsExpand) => {
    setExpandTimelineLayout(Boolean(needsExpand));
  }, []);

  const { userPermissions } = useAuth();

  const workroomModule = Object.hasOwn(userPermissions, MODULE_LIST.WORKROOM)
    ? userPermissions[MODULE_LIST.WORKROOM]
    : false;

  const activationModule = Object.hasOwn(
    userPermissions,
    MODULE_LIST.ACTIVATION
  )
    ? userPermissions[MODULE_LIST.ACTIVATION]
    : false;

  const canEditWorkroom = workroomModule.edit;
  const canEditActivation = activationModule.edit;

  const conceptTitle = workroom?.conceptTitle ?? '';
  const concept = workroom?.concept ?? '';
  const imageNames = useMemo(() => getImageNames(workroom), [workroom]);

  // create new or update
  async function handleActivation(form, isEditMode) {
    try {
      const formData = buildActivationFormData(form);
      const { activationId } = form;
      if (isEditMode && activationId) {
        await updateActivation(formData, activationId);
      } else {
        await createActivation(formData);
      }
      setSnackbar({
        open: true,
        message: isEditMode ? 'Activation Updated' : 'Activation Created',
      });
      setActivations((prev) => ({ ...prev, modal: false }));
      // loading fresh activations list, either on create or update
      loadActivations();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Something went wrong',
        severity: 'error',
      });
    }
  }

  const loadActivations = async () => {
    try {
      let response = await getActivations();
      response = response.filter((a) => a.projectId === Number(projectId));

      setActivations((prev) => ({ ...prev, list: response }));
    } catch (err) {
      console.error('Failed to load activations:', err);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    loadActivations();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setWorkroom(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadWorkroom = async () => {
      setLoading(true);

      try {
        const response = await getProjectWorkRoomByProjectId(projectId);
        if (!cancelled) {
          setWorkroom(unwrapWorkroom(response));
        }
      } catch {
        // No workroom yet — show empty placeholder UI, not an error.
        if (!cancelled) {
          setWorkroom(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadWorkroom();

    const onWorkroomUpdated = async () => {
      try {
        const response = await getProjectWorkRoomByProjectId(projectId);
        if (!cancelled) setWorkroom(unwrapWorkroom(response));
      } catch {
        if (!cancelled) setWorkroom(null);
      }
    };
    window.addEventListener(WORKROOM_UPDATED_EVENT, onWorkroomUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(WORKROOM_UPDATED_EVENT, onWorkroomUpdated);
    };
  }, [projectId]);

  const openWorkroomEdit = (step) => {
    if (workroom) {
      dispatchEditWorkroom({ workroom, step });
      return;
    }
    // No workroom yet: open the CreateWorkroomModal at the requested step.
    dispatchCreateWorkroom({ projectId, step });
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        flexGrow: 1,
        position: 'relative',
        height: '100%',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexShrink: 0,
          [`@media ${mobileNavMediaQuery}`]: { px: 0.5 },
        }}
      >
        <ModeButtons />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pl: expandTimelineLayout ? 30 : 35,
          pr: 3,
          py: 4,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: 10,
          },
          [`@media ${mobileNavMediaQuery}`]: {
            flexDirection: 'column',
            pl: 1.5,
            pr: 1.5,
            py: 2,
            gap: 2,
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            borderRadius: '16px',
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            px: 5,
            py: 5,
            minHeight: { xs: 'auto', md: '100%' },
            boxSizing: 'border-box',
            [`@media ${mobileNavMediaQuery}`]: {
              px: 2,
              py: 2.5,
              order: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 3,
              mb: 6,
              [`@media ${mobileNavMediaQuery}`]: {
                flexDirection: 'column',
                gap: 2.5,
                mb: 4,
              },
            }}
          >
            <Box
              sx={{
                maxWidth: '72%',
                minWidth: 0,
                [`@media ${mobileNavMediaQuery}`]: { maxWidth: '100%' },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  opacity: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                Concept:
              </Typography>

              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    my: 3,
                  }}
                >
                  <CircularProgress
                    size={18}
                    sx={{ color: 'rgba(255,255,255,0.5)' }}
                  />
                  <Typography sx={{ opacity: 0.6, fontSize: 14 }}>
                    Loading workroom...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      my: 2,
                      fontSize: { xs: '1.15rem', sm: '1.5rem' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {conceptTitle
                      ? `“${conceptTitle}”`
                      : 'No concept title yet'}
                  </Typography>

                  {concept ? (
                    <Box
                      sx={{
                        maxHeight: { xs: 180, sm: 220 },
                        overflowY: 'auto',
                        mb: 3,
                        pr: 1,
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: 'rgba(255,255,255,0.22)',
                          borderRadius: 10,
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255,255,255,0.22) transparent',
                      }}
                    >
                      <Typography
                        sx={{
                          opacity: 0.8,
                          fontSize: 14,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {concept.trim()}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        opacity: 0.55,
                        fontSize: 14,
                        lineHeight: 1.7,
                        mb: 3,
                      }}
                    >
                      No concept description yet.
                    </Typography>
                  )}

                  <Button
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    disabled={loading}
                    onClick={() => openWorkroomEdit(WORKROOM_STEP.CONCEPT)}
                    sx={{
                      display: canEditWorkroom ? 'flex' : 'none',
                      mt: 0.5,
                      color: 'rgba(255,255,255,0.42)',
                      textTransform: 'none',
                      px: 0,
                      '&:hover': { color: 'rgba(255,255,255,0.72)' },
                      '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    edit description
                  </Button>
                </>
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                [`@media ${mobileNavMediaQuery}`]: { alignSelf: 'flex-start' },
              }}
            >
              <ConceptImageGrid
                projectId={projectId}
                imageNames={imageNames}
                onAddRef={() =>
                  openWorkroomEdit(WORKROOM_STEP.REFERENCE_IMAGES)
                }
              />
            </Box>
          </Box>

          <Divider
            sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: { xs: 3, sm: 5 } }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: { xs: 2.5, sm: 4 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
              }}
            >
              Activations
            </Typography>
            <Button
              variant="text"
              sx={{
                color: 'rgba(255,255,255,0.42)',
                '&:hover': { color: 'rgba(255,255,255,0.65)' },
                textTransform: 'none',
                fontSize: 12,
                display: canEditActivation ? 'block' : 'none',
              }}
              onClick={() => {
                setActivations((prev) => ({ ...prev, modal: true }));
              }}
            >
              + New Activation
            </Button>
          </Box>

          <ActivationList
            activationList={activations.list}
            handleActivation={(activation) => {
              setActivations((prev) => ({
                ...prev,
                activeActivation: activation,
                viewMode: true,
              }));
            }}
          />
        </Box>

        <WorkroomStatusPanel
          onExpandedLayoutChange={handleTimelineExpandedLayout}
        />
        <ViewActivation
          data={activations.activeActivation}
          open={Boolean(activations.activeActivation && activations.viewMode)}
          onClose={() => {
            setActivations((prev) => ({
              ...prev,
              activeActivation: null,
              viewMode: false,
            }));
          }}
          onEditClick={(activation, step = 0) => {
            setActivations((prev) => ({
              ...prev,
              activeActivation: activation,
              modal: true,
              viewMode: false,
              defaultStep: step,
            }));
          }}
        />
        <ActivationForm
          open={Boolean(activations.modal && !activations.viewMode)}
          onSubmit={handleActivation}
          initialData={activations.activeActivation}
          defaultStep={activations.defaultStep}
          handleClose={() =>
            setActivations((prev) => ({
              ...prev,
              modal: false,
              activeActivation: null,
            }))
          }
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={() =>
            setSnackbar({ open: false, message: '', severity: 'success' })
          }
        >
          <Alert
            onClose={() =>
              setSnackbar({ open: false, message: '', severity: 'success' })
            }
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
